<script lang="ts">
  import { PUBLIC_S3_STATIC_FILES_URL } from '$env/static/public';

  import { formatExifDate } from '@shared/libs/tools';
  import { IconButton } from '@shared/ui/buttons/icon-button';
  import { Icon } from '@shared/ui/icon';
  import { HoverLink } from '@shared/ui/links/hover-link';
  import { Modal } from '@shared/ui/modal';
  import { Portal } from '@shared/ui/portal';

  import type { ArtAsset } from '@entities/art-asset';

  import DOLabel from './do-label.svelte';

  export let digitalOriginalData: ArtAsset['digital_original'];
  export let variant: 'primary' | 'secondary' | 'tertiary' = 'secondary';
  export let hasLabel = false;

  let showModal = false;

  const toggleModal = () => (showModal = !showModal);

  $: dateCreated = formatExifDate(digitalOriginalData?.exif?.CreateDate as string | null | undefined);

  $: photographer = digitalOriginalData?.photographer
    ? `${digitalOriginalData.photographer.first_name} ${digitalOriginalData.photographer.last_name}`
    : 'Digital Original Studio';
</script>

{#if hasLabel}
  <DOLabel on:click="{toggleModal}" {variant}>
    DO {digitalOriginalData?.mpx}Mpx/48Bit
  </DOLabel>
{:else}
  <button on:click="{toggleModal}">
    <slot />
  </button>
{/if}

<Portal>
  <Modal bind:open="{showModal}" outsideClose dismissible size="lg">
    <div slot="header">
      <div class="mx-6 mb-5 mt-8 flex items-start justify-between gap-2 md:mx-8">
        <div class="flex flex-wrap gap-3.5">
          <div class="text-blue-750 dark:text-blue-650">
            <Icon name="DO" width="{28}" height="{28}" />
          </div>
          <p class="font-nm text-default-900 dark:text-dark-900 text-lg font-bold tracking-tight md:text-2xl">
            Digital Original Standard
          </p>
        </div>

        <IconButton size="{20}" icon="closeBig" on:click="{toggleModal}" variant="quaternary" />
      </div>
    </div>

    <div class="font-nm mb-6">
      <ul class="w-full">
        {#if digitalOriginalData?.mpx}
          <li class="mb-1 w-full">
            <ul class="flex gap-x-0.5 text-sm sm:text-base">
              <li class="text-default-600 dark:text-dark-800 w-28 shrink-0 sm:w-32 md:w-1/2">Resolution</li>
              <li class="font-bold">{digitalOriginalData.mpx} MPX</li>
            </ul>
          </li>
        {/if}

        <li class="mb-1 w-full">
          <ul class="flex gap-x-0.5 text-sm sm:text-base">
            <li class="text-default-600 dark:text-dark-800 w-28 shrink-0 sm:w-32 md:w-1/2">Color depth</li>
            <li class="flex flex-wrap gap-x-2 gap-y-0 font-medium">
              <span>
                <b>48 bit</b>

                <img
                  src="{PUBLIC_S3_STATIC_FILES_URL}rainbow-icon_3x.png"
                  class="ml-0.5 inline-block h-[18px] w-[18px] align-text-top md:ml-1"
                  alt="Rainbow icon"
                  loading="lazy"
                />
              </span>

              <b>281 Trillion Colors</b>
            </li>
          </ul>
        </li>

        {#if digitalOriginalData?.exif?.FileSize}
          <li class="mb-1 w-full">
            <ul class="flex gap-x-0.5 text-sm sm:text-base">
              <li class="text-default-600 dark:text-dark-800 w-28 shrink-0 sm:w-32 md:w-1/2">Original file size</li>
              <li class="font-bold">{digitalOriginalData.exif.FileSize}</li>
            </ul>
          </li>
        {/if}

        <li class="mb-1 w-full">
          <ul class="flex gap-x-0.5 text-sm sm:text-base">
            <li class="text-default-600 dark:text-dark-800 w-28 shrink-0 sm:w-32 md:w-1/2">Photographer</li>
            <li class="font-bold">{photographer}</li>
          </ul>
        </li>

        {#if dateCreated}
          <li class="mb-1 w-full">
            <ul class="flex gap-x-0.5 text-sm sm:text-base">
              <li class="text-default-600 dark:text-dark-800 w-28 shrink-0 sm:w-32 md:w-1/2">Captured on</li>
              <li class="font-bold">{dateCreated}</li>
            </ul>
          </li>
        {/if}
      </ul>
    </div>

    <p class="font-nm mb-5 text-sm sm:text-base">
      The Digital Original Standard is a universal benchmark for digitizing and distributing physical art in the digital
      realm. Each image is captured, reviewed, and approved by a verified studio and evaluated by certified
      professionals at Digital Original, ensuring the highest possible quality.
    </p>

    <p class="tracking mb-5 flex items-center gap-2.5 text-base font-semibold">
      <Icon name="dr" width="{26}" height="{26}" />
      <span>Exclusive Rights to Own Digital</span>
    </p>

    <p class="font-nm mb-5 text-sm sm:text-base">
      Every purchase provides exclusive digital ownership, fully verified on the blockchain. This unique, non-duplicable
      ownership allows collectors to possess a rare and authentic digital counterpart, with rights that can be
      transferred or sold, offering unmatched value and exclusive right to own digital.
    </p>

    <HoverLink link="https://digitaloriginalstandard.org/" target="_blank">
      Visit <strong>digitaloriginalstandard.org</strong>
    </HoverLink>

    <div slot="footer" class="mb-8"></div>
  </Modal>
</Portal>
